import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ToolService } from "app/core/services/tool.service";
import { Subscription } from "rxjs";
import { Tool } from "@app/tool/model/tool.model";
import { Log } from "ng2-logger";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { FormType } from "@app/tool/details/components/form-type.enum";
import { LifecycleFormModalComponent } from "@app/tool/details/components/modals/lifecycle-form-modal/lifecycle-form-modal.component";

import * as _ from 'lodash';
import { ToolDetails } from "@app/tool/model/toolDetails.model";
import { DescriptionFormModalComponent } from "@app/tool/details/components/modals/description-form-modal/description-form-modal.component";
import { UserService } from "@services/user.service";
import { PermissionService } from "@app/authentication/services/permission.service";
import * as moment from "moment";
import { User } from "@app/shared/models";
import { timeUnits } from '@models/consts/timeUnits.const';
import { DropdownComponent } from '@components/form';
import { TooltipComponent } from '@app/shared/components';
import { ToolConfigWizardComponent } from './tool-config-wizard/tool-config-wizard.component';
import {
    SpecialIdentifiersFormModalComponent
} from "@app/tool/details/components/modals/special-identifiers-form-modal/special-identifiers-form-modal.component";
import { PendingToolRequestsModalComponent } from '@app/shared/pages/pending-tool-requests-modal/pending-tool-requests-modal.component';
import { PendingToolReturnsModalComponent } from "@app/shared/pages/pending-tool-returns-modal/pending-tool-returns-modal.component";
import { ToolReturnsService } from '@app/core/services/tool-returns.service';
import { ToolReturn } from '@app/shared/models/tool-return.model';
import { ToolRequest } from '@app/shared/models/toolRequests.model';
import { ToolRequestsService } from '@app/core/services/tool-requests.service';
import { ToolReturnTypeEnum } from '@app/shared/models/enums/tool-return-type.enum';
import { CODING_TYPE_LABELS, isNACodingType, normalizeCodingTypeValue } from '@app/shared/models/enums/codingType.enum';

@Component({
    selector: 'app-tool-details',
    templateUrl: './tool-details.component.html',
    styleUrls: ['./tool-details.component.scss']
})
export class SpecificToolDetailsComponent implements OnDestroy, AfterViewInit {
    _logger = Log.create(SpecificToolDetailsComponent.name);

    @ViewChild(LifecycleFormModalComponent, {static: false}) lifecycleFormModal: LifecycleFormModalComponent;
    @ViewChild(DescriptionFormModalComponent, {static: false}) descriptionFormModal: DescriptionFormModalComponent;
    @ViewChild(SpecialIdentifiersFormModalComponent, {static: false}) specialIdentificationsFormModal: SpecialIdentifiersFormModalComponent;
    @ViewChild('expirationTooltip', {static: false}) expirationTooltip: TooltipComponent;

    @ViewChild('lifetimeEstimationDropdown', {static: false}) lifetimeEstimationDropdown: DropdownComponent;
    @ViewChild('revisionTimeDropdown', {static: false}) revisionTimeDropdown: DropdownComponent;
    @ViewChild('revisionTimeContainer', {static: false}) revisionTimeContainer: ElementRef;
    @ViewChild('lifetimeTimeContainer', {static: false}) lifetimeTimeContainer: ElementRef;

    @ViewChild(ToolConfigWizardComponent, {static: false}) toolConfigWizard: ToolConfigWizardComponent;
    @ViewChild(PendingToolRequestsModalComponent, {static: false}) requestModalComponent: PendingToolRequestsModalComponent;
    @ViewChild(PendingToolReturnsModalComponent, {static: false}) returnModalComponent: PendingToolReturnsModalComponent;

    selectedTool: Tool;
    subscription: Subscription;
    remainingDaysExpiration;

    isLoading = false;

    // Tool details
    toolDetails: ToolDetails;
    toolCopy: Tool;

    // Styles for each of the 4 materials in the page
    toolDescriptionMaterialStyle = {height: '350px', minHeight: '300px'};
    toolLifecycleMaterialStyle = {height: '350px', minHeight: '300px'};
    toolSpecialIdentifierMaterialStyle = {height: '350px', minHeight: '300px'};
    toolConfigMaterialStyle = {height: '260px', minHeight: '600px'};

    // Owners
    owners: User[] = [];

    returnedTools = [];

    timeUnits = timeUnits;

    lifetimeEstimationSelectedUnit = 'minutes';
    revisionTimeSelectedUnit = 'minutes';

    lifetimeEstimationMultiplier = 1;
    revisionTimeMultiplier = 1;

    expirationStyles = {
        textColor: '',
        tooltipStyle: '',
        warningMessage: ''
    };

    lastLineReturn: any;
    lastFacilityReturn: any;
    lastLineRequest: any;
    lastFacilityRequest: any;

    constructor(private toolService: ToolService,
                private translateService: TranslateService,
                private userService: UserService,
                private permissionService: PermissionService,
                private toastr: ToastsManager,
                private toolReturnsService: ToolReturnsService,
                private toolRequestsService: ToolRequestsService) {
        this.subscribeToToolChanges();
        this.getOwners();
        this.translateTimeUnits();
    }

    get liftName() {
        return this.selectedTool && this.selectedTool.position && this.selectedTool.position.lift ?
            this.selectedTool.position.lift.name :
            this.translateService.instant('generic.notStored');
    }

    get trayName() {
        return this.selectedTool && this.selectedTool.position && this.selectedTool.position.tray ?
            this.selectedTool.position.tray.name :
            this.translateService.instant('generic.notStored');
    }

    get slotName() {
        return this.selectedTool && this.selectedTool.position && this.selectedTool.position.slot ?
            this.selectedTool.position.slot.name :
            this.translateService.instant('generic.notStored');
    }

    get totalLifeCyclesLabel(): string {
        return this.selectedTool?.details?.totalProductionLifeCycles
            ? `${this.selectedTool.details.totalProductionLifeCycles} ${this.translateService.instant('admin.tools.form.labels.cyclesUnit')}`
            : 'N/A';
    }

    get totalLifeTimeLabel(): string {
        return this.selectedTool?.details?.totalProductionLifeTime
            ? `${this.selectedTool.details.totalProductionLifeTime} ${this.translateService.instant('admin.tools.form.labels.timeUnit')}`
            : 'N/A';
    }

    get expirationDateLabel(): string {
        const expirationDate = this.selectedTool?.details?.expirationDate;
        if (!expirationDate) {
            return 'N/A';
        }

        const parsedDate = expirationDate instanceof Date
            ? moment(expirationDate)
            : moment(expirationDate, [
                moment.ISO_8601,
                'DD-MM-YYYY HH:mm',
                'DD/MM/YYYY HH:mm',
                'DD/MM/YYYY HH:mm a',
                'YYYY-MM-DDTHH:mm:ss.SSSZ'
            ], true);

        return parsedDate.isValid() ? parsedDate.format('DD-MM-YYYY') :
            String(expirationDate);
    }


    ngAfterViewInit() {
        this.translateService.stream(localStorage.getItem('user-language') || 'en').subscribe(() => this.setExpirationDateStyles());
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    openLifecycleModal() {
        this.toolDetails = _.cloneDeep(this.selectedTool.details);

        this.lifecycleFormModal.open();
    }

    openSpecialIdentificationsModal() {
        this.toolDetails = _.cloneDeep(this.selectedTool.details);

        this.specialIdentificationsFormModal.open();
    }

    openDescriptionModal() {
        this.getOwners();
        this.toolCopy = _.cloneDeep(this.selectedTool);
        this.descriptionFormModal.open();
    }

    openToolConfigWizard() {
        const tool = _.cloneDeep(this.selectedTool);
        this.toolConfigWizard.open(tool);
    }

    onUpdateToolDetails(toolDetails: any) {
        let toolPatch;
        switch (toolDetails.type) {
            case FormType.PART_NUMBER:
                toolPatch = {id: this.selectedTool.id, partNumberIds: toolDetails.partNumberIds};
                break;
            case FormType.EQUIPMENT:
                toolPatch = {id: this.selectedTool.id, equipmentIds: toolDetails.equipmentIds};
                break;
            case FormType.LIFECYCLE:
                toolPatch = Object.assign({id: this.selectedTool.id}, toolDetails.lifecycle);
                toolPatch.expirationDate = this.normalizeExpirationDate(toolPatch.expirationDate);
                break;
            case FormType.SPECIAL_IDENTIFIERS:
                toolPatch = Object.assign({id: this.selectedTool.id}, toolDetails.specialIdentifiers);
                break;
            case FormType.DESCRIPTION:
                toolPatch = Object.assign({id: this.selectedTool.id}, toolDetails.details);
                break;
            default:
                break;
        }
        this._logger.info('onUpdateToolDetails:: Patching tool with ', toolPatch, toolDetails.type);
        this.patchTool(toolPatch);
    }

    shouldRenderModifiers() {
        if (this.selectedTool) {
            return this.permissionService.hasHasPermissionForFeature('MANAGE_TOOLS') && !this.selectedTool.isDeleted;
        } else {
            return this.permissionService.hasHasPermissionForFeature('MANAGE_TOOLS')
        }
    }

    /**
     * Calculating remaining days from Current Date to Tool Expiration Date
     * */
    calculateRemainingDays() {
        const expirationDate = moment(this.selectedTool.details.expirationDate, 'DD/MM/YYYY HH:mm a').valueOf();
        const currentDate = moment().valueOf();
        const daysUntilExpiration = moment.duration(expirationDate - currentDate, 'milliseconds').asDays();

        this.remainingDaysExpiration = Math.floor(daysUntilExpiration) + 1;
        this.setExpirationDateStyles();
    };

    // TODO: Refactor OOP WAY
    setExpirationDateStyles() {
        if (this.remainingDaysExpiration > 30) {
            return this.expirationStyles = {
                textColor: '',
                tooltipStyle: '',
                warningMessage: ''
            };
        }
        if (this.remainingDaysExpiration >= 10 && this.remainingDaysExpiration <= 30) {
            return this.expirationStyles = {
                textColor: '#FFAB00',
                tooltipStyle: 'warning',
                warningMessage: this.translateService
                    .instant('admin.tools.details.detailsPage.labels.expirationWarning', {days: this.remainingDaysExpiration})
            };
        }

        if (this.remainingDaysExpiration < 10 && this.remainingDaysExpiration > 0) {
            return this.expirationStyles = {
                textColor: '#C62828',
                tooltipStyle: 'important',
                warningMessage: this.translateService
                    .instant('admin.tools.details.detailsPage.labels.expirationWarning', {days: this.remainingDaysExpiration})
            };
        }

        if (this.remainingDaysExpiration === 0) {
            return this.expirationStyles = {
                textColor: '#C62828',
                tooltipStyle: 'important',
                warningMessage: this.translateService.instant('admin.tools.details.detailsPage.labels.expirationWarningToday')
            };
        }

        if (this.remainingDaysExpiration < 0) {
            return this.expirationStyles = {
                textColor: '#C62828',
                tooltipStyle: 'important',
                warningMessage: this.translateService
                    .instant('admin.tools.details.detailsPage.labels.expirationAlreadyExpired', {days: this.remainingDaysExpiration * (-1)})
            };
        }
    }

    getCodingTypeLabel(): string {
        const codingType = normalizeCodingTypeValue(this.selectedTool?.codingType);

        if (isNACodingType(codingType)) {
            return 'N/A';
        }

        const label = CODING_TYPE_LABELS[codingType];

        return label ?? 'N/A';
    }

    private getToolDetails(id: number) {
        this._logger.info('getToolDetails:: Retrieving details for tool id', id);
        this.isLoading = true;
        this.toolService.getDetailsForTool(id)
            .subscribe(
                data => {
                    this._logger.info('getToolDetails:: API Returned data', data);
                    this.selectedTool.details = data;
                    this.calculateRemainingDays();
                    this.returnedTools.push(this.selectedTool);
                    this.returnedTools = [...this.returnedTools];
                    if (this.expirationTooltip) {
                        this.expirationTooltip.tooltipActions.emit({action: 'tooltip', params: ['']});
                    }
                    this.isLoading = false;
                },
                error => {
                    this._logger.error('getToolDetails:: API Returned error', error);
                    this.isLoading = false;
                }
            );
        this.toolService.getLastToolRequestById(id)
            .subscribe(
                (data: any) => {
                    this._logger.info('getLastToolRequestById:: API Returned data', data);
                    this.lastLineRequest = data.find(x => x.type === ToolReturnTypeEnum.LINE);
                    this.lastFacilityRequest = data.find(x => x.type === ToolReturnTypeEnum.TICKET);
                    this.isLoading = false;
                },
                error => {
                    this._logger.error('getLastToolRequestById:: API Returned error', error);
                    this.isLoading = false;
                }
            );
        this.toolService.getLastToolReturnById(id)
            .subscribe(
                (data: any) => {
                    this._logger.info('getLastToolReturnById:: API Returned data', data);
                    this.lastLineReturn = data.find(x => x.type === ToolReturnTypeEnum.LINE);
                    this.lastFacilityReturn = data.find(x => x.type === ToolReturnTypeEnum.TICKET);
                    this.isLoading = false;
                },
                error => {
                    this._logger.error('getLastToolReturnById:: API Returned error', error);
                    this.isLoading = false;
                }
            );
    }

    openLastReturn(event: any) {
        if (event === null || event === undefined || event.id === 0) {
            return;
        }
        this.toolReturnsService.getToolReturn(event.id, true).subscribe(
            (details: ToolReturn) => {
                this._logger.info('getToolReturnDetails:: API Returned data', details);
                this.returnModalComponent.open(details);
            },
            (error) => {
                this._logger.info('getToolReturnDetails:: API Returned error', error);
            }
        )
    }

    openLastRequest(event: any) {
        if (event === null || event === undefined || event.id === 0) {
            return;
        }
        this.toolRequestsService.getToolRequestDetails(event.id).subscribe(
            (details: ToolRequest) => {
                this._logger.info('getToolRequestDetails:: API Returned data', details);
                this.requestModalComponent.open(details);
            },
            (error) => {
                this._logger.info('getToolRequestDetails:: API Returned error', error);
            }
        )
    }

    private subscribeToToolChanges() {
        this._logger.info('subscribeToToolChanges:: Subscribing to toolsSelected$');
        this.isLoading = true;
        this.subscription = this.toolService.toolSelectedAsObservable.subscribe(
            (tool) => {
                this.selectedTool = tool;
                if (this.selectedTool && this.selectedTool.id) {
                    this.getToolDetails(this.selectedTool.id);
                    this.resetTimeUnits();
                    if (this.lifetimeEstimationDropdown && this.revisionTimeDropdown) {
                        this.lifetimeEstimationDropdown.selectActions.emit({action: 'material_select', params: ['']});
                        this.revisionTimeDropdown.selectActions.emit({action: 'material_select', params: ['']});
                    }
                }
            },
            (error) => {
                this._logger.error('subscribeToToolChanges:: Subscriber error', error);
                this.isLoading = false;
            }
        );
    }

    /**
     * Makes a request to the API to patch the tool with the modified properties.
     * The logic for handling the multiple tool details forms is centralized so that
     * all of them call this method.
     *
     * @param tool
     */
    private patchTool(tool: any) {
        this.isLoading = true;
        this.toolService.updateTool(tool)
            .subscribe(data => {
                this._logger.info('onUpdatePartNumbers:: API returned data', data);
                this.toastr.success(this.translateService.instant('admin.tools.toasts.updated',
                    {toolName: this.selectedTool.name}));
                this.closeModals();
                this.toolService.announceToolUpdate(this.selectedTool);
                this.isLoading = false;
            }, error => {
                this.closeModals();
                this.isLoading = false;
            });
    }

    private translateTimeUnits() {
        this.translateService.stream("timeUnits").subscribe(units => {
            this.timeUnits = this.timeUnits.map(timeUnit => {
                timeUnit.label = units[timeUnit.code];
                return timeUnit;
            });
        });
    }

    resetTimeUnits() {
        this.lifetimeEstimationSelectedUnit = 'minutes';
        this.lifetimeEstimationMultiplier = 1;
        this.revisionTimeSelectedUnit = 'minutes';
        this.revisionTimeMultiplier = 1;
    }

    private closeModals() {
        this._logger.info('closeModals::');
        try {
            this.lifecycleFormModal.close();
            this.specialIdentificationsFormModal.close();
            this.descriptionFormModal.close();
        } catch (e) {
            // Just don't want the error thrown
        }
    }

    private getOwners() {
        this._logger.info('getOwners:: Retrieving list of owners');
        this.userService.getUsers().subscribe((data) => {
                this._logger.info('getOwners:: API returned data', data);
                this.owners = data.users;
        });
    }

    private normalizeExpirationDate(expirationDate: any): number | null {
        if (_.isNil(expirationDate) || expirationDate === '') {
            return null;
        }

        if (expirationDate instanceof Date) {
            return expirationDate.getTime();
        }

        if (typeof expirationDate === 'string') {
            const numericValue = Number(expirationDate);
            if (!Number.isNaN(numericValue)) {
                return numericValue;
            }

            const parsedDate = moment(expirationDate);
            return parsedDate.isValid() ? parsedDate.valueOf() : null;
        }

        if (typeof expirationDate === 'number') {
            return expirationDate;
        }

        return null;
    }
}
